"use client";

import React from "react";
import { Clock, Monitor, Tag, CheckCircle2, BookOpen, Award, Calendar, ArrowRight, MessageCircle } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Separator } from "@/components/ui/separator";
const courseModules = [{
  id: "mod-1",
  title: "Módulo 1: Introducción a la Avicultura y Biología",
  lessons: ["Historia y situación actual de la avicultura", "Anatomía y fisiología externa e interna", "Razas y líneas genéticas comerciales", "Sistemas de producción: piso, jaula y pastoreo"]
}, {
  id: "mod-2",
  title: "Módulo 2: Manejo de Aves de Engorde y Postura",
  lessons: ["Recepción del pollito de un día", "Manejo de la alimentación y programas de luz", "Producción de huevo comercial y manejo de gallinas", "Control de temperatura y ventilación en galpones"]
}, {
  id: "mod-3",
  title: "Módulo 3: Nutrición y Sanidad Animal",
  lessons: ["Requerimientos nutricionales y formulación básica", "Bioseguridad y prevención de enfermedades", "Calendarios de vacunación y medicación", "Identificación y control de patologías comunes"]
}, {
  id: "mod-4",
  title: "Módulo 4: Gestión y Comercialización",
  lessons: ["Registros técnicos y económicos de la granja", "Bienestar animal y normativas vigentes", "Procesamiento y calidad de productos avícolas", "Estrategias de mercado y canales de venta"]
}];
export default function DetallesDelCurso({
  isBusinessCourse = false
}: {
  isBusinessCourse?: boolean;
}) {
  const courseDetails = [{
    icon: Clock,
    label: "Duración",
    value: "12 Semanas (120h)"
  }, {
    icon: Monitor,
    label: "Modalidad",
    value: isBusinessCourse ? "100% Online en Vivo" : "100% Presencial en Vivo"
  }, {
    icon: Calendar,
    label: "Inicio",
    value: "15 de Noviembre"
  }, {
    icon: Tag,
    label: "Inversión",
    value: "$20.000"
  }];
  return <section id="detalles-del-curso" className="bg-background py-16 md:py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-secondary/5 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          
          {}
          <div className="lg:col-span-8 space-y-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4">
                Programa del Curso
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl">
                Un plan de estudios estructurado paso a paso, diseñado para llevarte desde los conceptos básicos hasta el dominio avanzado de la materia.
              </p>
            </div>

            {}
            <div className="bg-background/40 backdrop-blur-[12px] border border-border/60 rounded-[24px] p-4 md:p-6 shadow-sm">
              <Accordion type="multiple" defaultValue={["mod-1"]} className="w-full">
                {courseModules.map((module, index) => <AccordionItem key={module.id} value={module.id} data-index={index} className="border-b border-border/40 last:border-0 px-2 md:px-4">
                    <AccordionTrigger className="font-sans font-bold text-base md:text-lg text-foreground hover:text-primary hover:no-underline transition-colors py-6">
                      <div className="flex items-center gap-3 text-left">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                          <BookOpen className="w-4 h-4" />
                        </div>
                        {module.title}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-6 pt-2 pl-11 md:pl-15">
                      <ul className="space-y-3">
                        {module.lessons.map((lesson, lessonIndex) => <li key={lessonIndex} data-index={lessonIndex} className="flex items-start gap-3 text-muted-foreground">
                            <CheckCircle2 className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                            <span className="text-sm md:text-base leading-relaxed">{lesson}</span>
                          </li>)}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>
          </div>

          {}
          <div className="lg:col-span-4">
            <div className="sticky top-24">
              <Card className="border-border/50 bg-background/80 backdrop-blur-xl transition-all duration-500 hover:shadow-[0_0_40px_-10px_var(--color-primary)] hover:border-primary/30 overflow-hidden group">
                {}
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary/50 via-secondary/50 to-primary/50 opacity-50 group-hover:opacity-100 transition-opacity duration-500" />
                
                <CardHeader className="pb-6 pt-8">
                  <CardTitle className="text-2xl font-bold text-center flex items-center justify-center gap-2">
                    <Award className="w-6 h-6 text-primary" />
                    Resumen Técnico
                  </CardTitle>
                </CardHeader>
                
                <CardContent className="space-y-0 px-6 md:px-8">
                  {courseDetails.map((detail, index) => <React.Fragment key={index}>
                      <div data-index={index} className="flex items-center justify-between py-4 group/item">
                        <div className="flex items-center gap-3 text-muted-foreground">
                          <detail.icon className="w-5 h-5 text-primary/70 group-hover/item:text-primary transition-colors" />
                          <span className="font-medium text-sm">{detail.label}</span>
                        </div>
                        <span className="font-mono font-semibold text-foreground text-right">
                          {detail.value}
                        </span>
                      </div>
                      {index < courseDetails.length - 1 && <Separator className="bg-border/50" />}
                    </React.Fragment>)}
                </CardContent>
                
                <CardFooter className="pt-6 pb-10 px-6 md:px-8 flex flex-col gap-4">
                  <Link to="https://mpago.la/1WfMEZk" className="w-full" newTab>
                    <Button size="lg" className="w-full h-14 text-lg font-bold rounded-xl bg-[#009EE3] hover:bg-[#008ED2] text-white border-none shadow-lg shadow-[#009EE3]/20 transition-all duration-300 hover:-translate-y-0.5 group">
                      Pagar con Mercado Pago
                      <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                  <Link to="https://wa.me/59896344395" className="w-full" newTab>
                    <Button size="lg" className="w-full h-14 text-lg font-bold rounded-xl bg-[#25D366] hover:bg-[#22c35e] text-white border-none shadow-lg shadow-[#25D366]/20 transition-all duration-300 hover:-translate-y-0.5">
                      <MessageCircle className="w-5 h-5 mr-2" />
                      Consultar por WhatsApp
                    </Button>
                  </Link>
                </CardFooter>
              </Card>
            </div>
          </div>

        </div>
      </div>
    </section>;
}